<template>
  <a-modal
    :visible="visible"
    title="创建新航线"
    width="min(900px, calc(100vw - 48px))"
    :footer="null"
    :mask-closable="false"
    :destroy-on-close="true"
    wrap-class-name="wl-create"
    @cancel="emit('update:visible', false)"
  >
    <div class="wl-create__body">
      <section class="wl-create__group">
        <div class="wl-create__types">
          <div class="wl-create__type-col">
            <h5>巡逻巡检航线</h5>
            <div class="wl-create__cards">
              <button type="button" class="wl-create__card" :class="{ active: form.templateType === 'waypoint' }" @click="form.templateType = 'waypoint'">
                <svg viewBox="0 0 32 32"><path d="M5 24 C10 8, 16 26, 22 12 S 28 6, 27 8" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" /><circle cx="5" cy="24" r="2.5" fill="currentColor" /><circle cx="22" cy="12" r="2.5" fill="currentColor" /></svg>
                <span>航点航线</span>
              </button>
              <a-tooltip title="巡逻航线需要在飞行器上录制，当前版本暂不支持">
                <button type="button" class="wl-create__card" disabled>
                  <svg viewBox="0 0 32 32"><rect x="6" y="6" width="20" height="20" rx="3" fill="none" stroke="currentColor" stroke-width="2" stroke-dasharray="4 3" /><path d="M11 16 L21 16 M16 11 L16 21" stroke="currentColor" stroke-width="2" /></svg>
                  <span>巡逻航线</span>
                </button>
              </a-tooltip>
            </div>
          </div>
          <div class="wl-create__type-col">
            <h5>测绘航线</h5>
            <div class="wl-create__cards">
              <button type="button" class="wl-create__card" :class="{ active: form.templateType === 'mapping2d' }" @click="form.templateType = 'mapping2d'">
                <svg viewBox="0 0 32 32"><path d="M6 8 H26 M6 14 H26 M6 20 H26 M6 26 H26" stroke="currentColor" stroke-width="2" stroke-linecap="round" /><path d="M26 8 V14 M6 14 V20 M26 20 V26" stroke="currentColor" stroke-width="2" /></svg>
                <span>面状航线</span>
              </button>
              <a-tooltip title="带状航线将在后续版本支持">
                <button type="button" class="wl-create__card" disabled>
                  <svg viewBox="0 0 32 32"><path d="M6 26 C10 14, 18 22, 26 6" fill="none" stroke="currentColor" stroke-width="6" stroke-linecap="round" opacity="0.4" /><path d="M6 26 C10 14, 18 22, 26 6" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" /></svg>
                  <span>带状航线</span>
                </button>
              </a-tooltip>
            </div>
          </div>
          <div class="wl-create__type-col wl-create__type-col--wide">
            <h5>精细化测绘航线</h5>
            <div class="wl-create__cards">
              <a-tooltip v-for="item in fineTypes" :key="item" title="需要三维模型交互，当前版本暂不支持">
                <button type="button" class="wl-create__card" disabled>
                  <svg viewBox="0 0 32 32"><path d="M8 24 L16 8 L24 24 Z" fill="none" stroke="currentColor" stroke-width="2" stroke-linejoin="round" /><path d="M12 16 H20" stroke="currentColor" stroke-width="2" /></svg>
                  <span>{{ item }}</span>
                </button>
              </a-tooltip>
            </div>
          </div>
        </div>
      </section>

      <section class="wl-create__group">
        <h5>创建方式</h5>
        <div class="wl-create__segmented">
          <button type="button" :class="{ active: form.mode === 'dock' }" @click="form.mode = 'dock'"><HomeOutlined /> 基于机场创建（DJI Dock 3）</button>
          <button type="button" :class="{ active: form.mode === 'manual' }" @click="form.mode = 'manual'"><RocketOutlined /> 选择飞行器</button>
        </div>

        <template v-if="form.mode === 'dock'">
          <div class="wl-create__dock-head">
            <span>选择机场，机场位置将直接作为参考起飞点，安全起飞高默认 {{ DOCK_TAKEOFF_SECURITY_HEIGHT }} m。</span>
            <a-button size="small" type="link" :loading="loadingDocks" @click="loadDocks(true)"><ReloadOutlined /> 刷新</a-button>
          </div>
          <a-spin :spinning="loadingDocks">
            <p v-if="!docks.length && !loadingDocks" class="wl-create__empty">{{ dockError || '当前工作空间没有绑定机场。请先在“设备管理”中绑定 DJI Dock 3。' }}</p>
            <div v-else class="wl-create__docks">
              <button
                v-for="dock in docks"
                :key="dock.sn"
                type="button"
                class="wl-create__dock"
                :class="{ active: form.dockSn === dock.sn, unsupported: !dock.droneModelKey }"
                :disabled="!dock.droneModelKey"
                @click="form.dockSn = dock.sn"
              >
                <span class="wl-create__dock-name">{{ dock.name }}</span>
                <span class="wl-create__dock-model">{{ dock.modelName }} · {{ dock.droneName }}</span>
                <span class="wl-create__dock-state">
                  <i :class="dock.online ? 'on' : 'off'" />{{ dock.online ? '在线' : '离线' }}
                  <template v-if="dock.position">· 已获取位置 {{ dock.position.latitude.toFixed(5) }}, {{ dock.position.longitude.toFixed(5) }}</template>
                  <template v-else>· 未上报位置</template>
                </span>
                <span v-if="!dock.droneModelKey" class="wl-create__dock-warn">配对飞行器不受编辑器支持</span>
              </button>
            </div>
          </a-spin>
          <p v-if="selectedDock && !selectedDock.position" class="wl-create__warn"><ExclamationCircleFilled /> 该机场尚未上报位置，创建后请在编辑器中手动设置起飞点，或等机场上线后在“航线设置”里重设。</p>
        </template>

        <template v-else>
          <h5 class="wl-create__sub">选择飞行器</h5>
          <div class="wl-create__chips">
            <button v-for="series in DRONE_SERIES" :key="series" type="button" class="wl-create__chip" :class="{ active: form.series === series }" @click="onSeriesChange(series)">{{ series }}</button>
            <a-tooltip v-for="series in unsupportedSeries" :key="series" title="该系列航线暂不支持在此编辑器中规划"><button type="button" class="wl-create__chip" disabled>{{ series }}</button></a-tooltip>
          </div>
          <h5 class="wl-create__sub">选择型号</h5>
          <div class="wl-create__chips">
            <button v-for="profile in models" :key="profile.key" type="button" class="wl-create__chip" :class="{ active: form.droneModelKey === profile.key }" @click="form.droneModelKey = profile.key">{{ profile.name }}</button>
          </div>
          <p class="wl-create__hint">型号决定负载、云台范围与相机参数，请在规划前选择正确的飞行器型号。</p>
        </template>
      </section>

      <section class="wl-create__group">
        <h5>航线名称</h5>
        <a-input :value="form.name" :maxlength="WAYLINE_NAME_MAX_LENGTH" show-count placeholder="请输入航线名称（60 字以内）" @update:value="onNameInput" @pressEnter="submit" />
        <p v-if="nameError" class="wl-create__error">{{ nameError }}</p>
      </section>
    </div>
    <div class="wl-create__footer">
      <a-button @click="emit('update:visible', false)">取消</a-button>
      <a-button type="primary" :disabled="!canSubmit" @click="submit">确定</a-button>
    </div>
  </a-modal>
</template>

<script lang="ts" setup>
/* global defineEmits, defineProps */
import { computed, reactive, ref, watch } from 'vue'
import { ExclamationCircleFilled, HomeOutlined, ReloadOutlined, RocketOutlined } from '@ant-design/icons-vue'
import type { Device } from '/@/types/device'
import { EDeviceTypeName, ELocalStorageKey } from '/@/types/enums'
import { getBindingDevices } from '/@/api/manage'
import { useMyStore } from '/@/store'
import type { WaylineTemplateType } from '/@/types/wayline-editor'
import { DOCK_TAKEOFF_SECURITY_HEIGHT, DRONE_PROFILES, DRONE_SERIES, WAYLINE_NAME_MAX_LENGTH, buildDockOption, createDraft, findDroneProfile } from '/@/utils/wayline-editor'
import { sanitizeWaylineName } from '/@/utils/flight-record'

const props = defineProps<{ visible: boolean; author: string }>()
const emit = defineEmits(['update:visible', 'create'])

const store = useMyStore()
const workspaceId = localStorage.getItem(ELocalStorageKey.WorkspaceId) || ''
const fineTypes = ['斜面航线', '几何体航线', '贴近摄影航线']
const unsupportedSeries = ['Matrice 4 行业系列', 'Matrice 400']
const form = reactive({
  templateType: 'waypoint' as WaylineTemplateType,
  mode: 'dock' as 'dock' | 'manual',
  dockSn: '',
  series: DRONE_SERIES[0],
  droneModelKey: DRONE_PROFILES[0].key,
  name: '',
})
const nameError = ref('')
const rawDocks = ref<Device[]>([])
const loadingDocks = ref(false)
const dockError = ref('')
const models = computed(() => DRONE_PROFILES.filter(profile => profile.series === form.series))
// Dock positions arrive over the OSD websocket, so the cards follow the store live.
const docks = computed(() => rawDocks.value.map(device => buildDockOption(device, store.state.deviceState.dockInfo[device.device_sn]?.basic_osd)))
const selectedDock = computed(() => docks.value.find(dock => dock.sn === form.dockSn) || null)
const canSubmit = computed(() => form.mode === 'manual' || Boolean(selectedDock.value?.droneModelKey))

watch(() => props.visible, visible => {
  if (!visible) return
  nameError.value = ''
  form.name = defaultName()
  loadDocks(false)
}, { immediate: true })

watch(selectedDock, dock => {
  if (dock && form.mode === 'dock' && (!form.name || form.name.startsWith('新建航线') || /航线 \d/.test(form.name))) form.name = defaultName()
})

function defaultName (): string {
  const stamp = new Date().toLocaleDateString()
  return form.mode === 'dock' && selectedDock.value ? `${selectedDock.value.name} 航线 ${stamp}` : `新建航线 ${stamp}`
}

async function loadDocks (force: boolean) {
  if (loadingDocks.value || (rawDocks.value.length && !force)) return
  loadingDocks.value = true
  dockError.value = ''
  try {
    const result = await getBindingDevices(workspaceId, { page: 1, page_size: 100, total: 0 }, EDeviceTypeName.Dock)
    if (result.code === 0) {
      rawDocks.value = result.data.list
      // DJI Dock 3 first, then anything else the workspace has bound.
      rawDocks.value.sort((a, b) => Number(b.type === 3) - Number(a.type === 3))
      if (!form.dockSn) {
        const first = rawDocks.value.map(device => buildDockOption(device, store.state.deviceState.dockInfo[device.device_sn]?.basic_osd)).find(dock => dock.droneModelKey)
        form.dockSn = first?.sn || ''
      }
    } else {
      dockError.value = result.message || '读取机场列表失败。'
    }
  } catch (error) {
    dockError.value = (error as Error).message || '读取机场列表失败，请检查网络后重试。'
  } finally {
    loadingDocks.value = false
  }
}

function onSeriesChange (series: string) {
  form.series = series
  form.droneModelKey = DRONE_PROFILES.find(profile => profile.series === series)?.key || DRONE_PROFILES[0].key
}

function onNameInput (value: string) {
  form.name = value
  nameError.value = ''
}

function submit () {
  const name = sanitizeWaylineName(form.name)
  if (!name) { nameError.value = '请输入航线名称'; return }
  if (name.length > WAYLINE_NAME_MAX_LENGTH) { nameError.value = `航线名称不能超过 ${WAYLINE_NAME_MAX_LENGTH} 个字符`; return }
  const dock = form.mode === 'dock' ? selectedDock.value : null
  if (form.mode === 'dock' && (!dock || !dock.droneModelKey)) { nameError.value = '请选择一个机场'; return }
  const profile = findDroneProfile(dock?.droneModelKey || form.droneModelKey) || DRONE_PROFILES[0]
  const draft = createDraft({
    name,
    templateType: form.templateType,
    droneModelKey: profile.key,
    payloadModelKey: profile.payloads[0].key,
    author: props.author,
    dock: dock ? { sn: dock.sn, name: dock.name, modelKey: dock.modelKey } : null,
    takeOffRefPoint: dock?.position || null,
  })
  emit('create', draft)
  emit('update:visible', false)
}
</script>

<style lang="scss">
.wl-create {
  .ant-modal-content { background: #1b1c1f; color: #e9eaec; border: 1px solid #35373c; }
  .ant-modal-header { background: #1b1c1f; border-bottom: 1px solid #35373c; text-align: center; }
  .ant-modal-title { color: #fff; }
  .ant-modal-close-x { color: #c9ccd2; }
  .ant-modal-body { padding: 16px 20px 8px; }
  .ant-input, .ant-input-affix-wrapper { background: #101113; color: #fff; border-color: #35373c; }
  .ant-input-show-count-suffix { color: #9a9ea6; }
  h5 { margin: 0 0 8px; color: #9a9ea6; font-size: 12px; font-weight: 500; }
  &__body { display: flex; flex-direction: column; gap: 12px; }
  &__group { padding: 12px 14px; border-radius: 6px; background: #24262a; }
  &__types { display: flex; flex-wrap: wrap; gap: 12px 20px; }
  &__type-col { flex: 2 1 260px; min-width: 0; }
  &__type-col--wide { flex: 3 1 400px; }
  &__cards { display: flex; flex-wrap: wrap; gap: 10px; }
  &__card { display: flex; flex-direction: column; align-items: center; justify-content: center; gap: 6px; flex: 1 1 120px; max-width: 160px; height: 82px; border: 1px solid #35373c; border-radius: 6px; background: #2c2d31; color: #e9eaec; cursor: pointer; }
  &__card svg { width: 28px; height: 28px; }
  &__card:hover:not(:disabled) { border-color: #2d8cf0; }
  &__card.active { background: #1e3a5f; border-color: #2d8cf0; color: #fff; }
  &__card:disabled { color: #6b6f77; cursor: not-allowed; }
  &__segmented { display: flex; gap: 8px; margin-bottom: 12px; }
  &__segmented button { display: inline-flex; align-items: center; gap: 6px; height: 34px; padding: 0 16px; border: 1px solid #35373c; border-radius: 4px; background: #2c2d31; color: #e9eaec; cursor: pointer; }
  &__segmented button.active { background: #2d8cf0; border-color: #2d8cf0; color: #fff; }
  &__dock-head { display: flex; align-items: center; justify-content: space-between; margin-bottom: 8px; color: #c9ccd2; }
  &__docks { display: grid; grid-template-columns: repeat(auto-fill, minmax(260px, 1fr)); gap: 10px; }
  &__dock { display: flex; flex-direction: column; align-items: flex-start; gap: 4px; padding: 10px 12px; border: 1px solid #35373c; border-radius: 6px; background: #2c2d31; color: #e9eaec; cursor: pointer; text-align: left; }
  &__dock:hover:not(:disabled) { border-color: #2d8cf0; }
  &__dock.active { background: #1e3a5f; border-color: #2d8cf0; }
  &__dock:disabled { cursor: not-allowed; opacity: 0.6; }
  &__dock-name { color: #fff; font-size: 13px; font-weight: 600; }
  &__dock-model { color: #c9ccd2; }
  &__dock-state { color: #9a9ea6; font-size: 11px; }
  &__dock-state i { display: inline-block; width: 7px; height: 7px; margin-right: 4px; border-radius: 50%; background: #6b6f77; }
  &__dock-state i.on { background: #19be6b; }
  &__dock-warn { color: #ff9f1a; font-size: 11px; }
  &__empty { margin: 8px 0; color: #9a9ea6; }
  &__warn { margin: 10px 0 0; color: #f2d299; }
  &__sub { margin-top: 4px; }
  &__chips { display: flex; flex-wrap: wrap; gap: 8px; margin-bottom: 10px; }
  &__chip { height: 40px; padding: 0 22px; border: 1px solid #35373c; border-radius: 4px; background: #2c2d31; color: #e9eaec; cursor: pointer; }
  &__chip:hover:not(:disabled) { border-color: #2d8cf0; }
  &__chip.active { background: #1e3a5f; border-color: #2d8cf0; color: #fff; }
  &__chip:disabled { color: #6b6f77; cursor: not-allowed; }
  &__hint { margin: 0; color: #9a9ea6; }
  &__error { margin: 6px 0 0; color: #ffb4a9; }
  &__footer { display: flex; justify-content: flex-end; gap: 10px; padding: 12px 0 4px; }
}
</style>
